// src/services/api.ts

import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  User,
  SocialAccount,
  InstagramAccount,
  DMTemplate,
  UsageStats,
  ConversationStats,
} from '../types';

// API calls go to production backend URL
const API_BASE_URL = 'https://chatutu-api-prod.r-aleyasin.workers.dev';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        // Skip ngrok warning page
        'ngrok-skip-browser-warning': 'true',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(email: string, password: string) {
    const response = await this.client.post('/api/auth/register', { email, password });
    return response.data;
  }

  async login(email: string, password: string) {
    const response = await this.client.post('/api/auth/login', { email, password });
    return response.data;
  }

  async logout() {
    const response = await this.client.post('/api/auth/logout');
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/api/auth/me');
    return response.data;
  }

  // Social Account endpoints
  async connectSocialAccount(platform: string, code: string, state: string) {
    const response = await this.client.post(`/api/${platform}/connect?code=${code}&state=${state}`);
    return response.data;
  }

  async getSocialAccounts(platform?: string): Promise<SocialAccount[]> {
    const url = platform ? `/api/${platform}/accounts` : '/api/accounts';
    const response = await this.client.get(url);
    return response.data;
  }

  async disconnectSocialAccount(platform: string, accountId: string) {
    const response = await this.client.delete(`/api/${platform}/accounts/${accountId}`);
    return response.data;
  }

  // Legacy aliases for backward compatibility
  async connectInstagram(code: string, state: string) {
    return this.connectSocialAccount('instagram', code, state);
  }

  async getInstagramAccounts(): Promise<InstagramAccount[]> {
    return this.getSocialAccounts('instagram') as Promise<InstagramAccount[]>;
  }

  async disconnectInstagramAccount(accountId: string) {
    return this.disconnectSocialAccount('instagram', accountId);
  }

  // Template endpoints
  async createTemplate(template: Partial<DMTemplate>) {
    const response = await this.client.post('/api/templates', template);
    return response.data;
  }

  async getTemplates(accountId?: string): Promise<DMTemplate[]> {
    const url = accountId
      ? `/api/templates?instagram_account_id=${accountId}`
      : '/api/templates';
    const response = await this.client.get(url);
    return response.data;
  }

  async updateTemplate(templateId: string, updates: Partial<DMTemplate>) {
    const response = await this.client.put(`/api/templates/${templateId}`, updates);
    return response.data;
  }

  async deleteTemplate(templateId: string) {
    const response = await this.client.delete(`/api/templates/${templateId}`);
    return response.data;
  }

  // Conversation endpoints
  async getConversations(accountId?: string, limit = 20, offset = 0) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (accountId) params.append('instagram_account_id', accountId);
    const response = await this.client.get(`/api/conversations?${params}`);
    return response.data;
  }

  async getConversationMessages(conversationId: string, limit = 50, offset = 0) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const response = await this.client.get(`/api/conversations/${conversationId}/messages?${params}`);
    return response.data;
  }

  async sendMessage(conversationId: string, content: string, templateId?: string) {
    const response = await this.client.post(`/api/conversations/${conversationId}/messages`, {
      content,
      use_template_id: templateId,
    });
    return response.data;
  }

  // Analytics endpoints
  async getUsageStats(period = 'month', accountId?: string): Promise<UsageStats> {
    const params = new URLSearchParams({ period });
    if (accountId) params.append('instagram_account_id', accountId);
    const response = await this.client.get(`/api/analytics/usage?${params}`);
    return response.data;
  }

  async getConversationStats(): Promise<ConversationStats> {
    const response = await this.client.get('/api/analytics/conversations');
    return response.data;
  }

  // Subscription endpoints
  async upgradeSubscription(planId: number) {
    const response = await this.client.post('/api/subscriptions/upgrade', { plan_id: planId });
    return response.data;
  }

  async getCurrentSubscription() {
    const response = await this.client.get('/api/subscriptions/current');
    return response.data;
  }
}

export const api = new ApiClient();
